import { PlusOutlined } from '@ant-design/icons';
import { ScopeButton } from '@monorepo/shared-lib';
import GenTaxLogo from 'assets/images/gt-logo-menu-small.svg';
import IconSage from 'assets/images/sage_icon.png';
import { CardDescSpan } from 'components/Custom/spans';
import { MessageStates } from 'containers/MessageBox';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { turnDrawerOff, turnMessageOn, turnModalOn } from 'store/app/actions';
import { UploadTransactionsSelection, WindowType } from 'store/app/enums';
import { setUploadTransactionsTemp } from 'store/organisation/actions';
import styled from 'styled-components';
import type * as XLSXTypes from 'xlsx';
import { UploadTransactionsColumnsModal } from './upload-transactions-columns-modal';

const DrawerContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0;
`;

const InstructionText = styled(CardDescSpan)`
  margin-bottom: 24px;
  color: var(--color-text-muted, #5a6a6a);
  font-size: 14px;
`;

const ClickableLink = styled.span`
  color: var(--color-primary, #41aaaf);
  text-decoration: underline;
  cursor: pointer;
  font-weight: 500;
  &:hover {
    color: var(--color-primary-hover, #64bbbd);
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  width: 100%;
  justify-items: center;
  margin-bottom: 20px;
`;

const UploadButton = styled.div<{ $isSelected?: boolean }>`
  width: 99px;
  height: 99px;
  box-shadow: 0px 0px 10px 0px #0000000d;
  border: ${(props) =>
    props.$isSelected ? '2px solid var(--color-primary, #41aaaf)' : '1px solid var(--color-border, #dbeae3)'};
  background: var(--color-surface-raised, #ffffff);
  border-radius: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: border 0.2s ease;
  &:hover {
    border: ${(props) =>
      props.$isSelected ? '2px solid var(--color-primary, #41aaaf)' : '1px solid var(--color-primary, #41aaaf)'};
  }
`;

const ButtonContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 8px;
`;

const ButtonText = styled.span`
  font-family: Inter;
  font-weight: 400;
  font-style: Regular;
  font-size: 12px;
  line-height: 16px;
  color: var(--color-text-muted, #5a6a6a);
  text-align: center;
`;

const LogoImage = styled.img`
  max-width: 70px;
  max-height: 50px;
  object-fit: contain;
`;

const GenTaxLogoImage = styled.img`
  width: 53px;
  height: 45px;
  margin-bottom: 4px;
  object-fit: contain;
`;

const FileUploadSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 24px;
  gap: 16px;
`;

const FileUploadText = styled(CardDescSpan)`
  font-family: Inter;
  font-weight: 400;
  font-style: Regular;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  color: var(--color-text-dark, #042a2a);
  margin-bottom: 0;
`;

const FileInputWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 45px;
  border: 1px solid var(--color-border, #dbeae3);
  border-radius: 50px;
  background: var(--color-surface-raised, #ffffff);
  position: relative;
  overflow: hidden;
`;

const FileInput = styled.input`
  flex: 1;
  height: 100%;
  padding: 8px 12px;
  padding-right: 50px;
  border: none;
  font-family: Inter;
  font-weight: 400;
  font-style: Regular;
  font-size: 14px;
  line-height: 24px;
  color: var(--color-text-muted, #5a6a6a);
  background: transparent;
  cursor: default;

  &::placeholder {
    color: var(--color-text-muted, #5a6a6a);
  }

  &:focus {
    outline: none;
  }
`;

const FileInputButton = styled.button`
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background-color: var(--color-primary, #41aaaf);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #ffffff;
  font-size: 16px;
  flex-shrink: 0;

  &:hover {
    background-color: var(--color-primary-hover, #64bbbd);
  }

  &:focus {
    outline: none;
  }
`;

const ErrorMessage = styled.div`
  color: #d13131;
  font-family: Inter;
  font-weight: 400;
  font-style: Regular;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0%;
  text-align: center;
  margin-top: -8px;
`;

const UploadFileButtonWrapper = styled.div`
  width: 100%;
  margin-top: 25px;
`;

const DescriptionPlaceholder = styled.div`
  width: 100%;
  min-height: 60px;
  height: 60px;
  padding: 12px;
  margin-bottom: 16px;
  text-align: left;
  font-family: Inter;
  font-weight: 400;
  font-style: Regular;
  font-size: 14px;
  line-height: 20px;
  color: var(--color-text-dark, #042a2a);
  display: flex;
  align-items: flex-start;
`;

const ALLOWED_UPLOAD_EXTENSIONS = ['.xlsx', '.xls', '.csv'];

/** Support article URL for Excel / CSV upload requirements. */
const SUPPORT_ARTICLE_URL =
  'https://help.generate.tax/portal/en/kb/articles/import-data-using-excel-csv';

/** Check if file is Excel or CSV by extension (accept can be bypassed in file picker). */
export const isValidExcelOrCsvFile = (file: File): boolean => {
  const name = file.name?.toLowerCase() ?? '';
  return ALLOWED_UPLOAD_EXTENSIONS.some((ext) => name.endsWith(ext));
};

/** Exported for direct file-picker flow (e.g. from Upload File button without opening drawer). */
export const extractColumnNamesFromFile = async (
  file: File
): Promise<string[]> => {
  const XLSX = await import('xlsx');
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        if (!data) {
          reject(new Error('Failed to read file'));
          return;
        }
        let workbook: XLSXTypes.WorkBook;
        if (file.name.endsWith('.csv')) {
          workbook = XLSX.read(data as string, { type: 'string' });
        } else {
          workbook = XLSX.read(data as string, { type: 'binary' });
        }
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: '',
        }) as any[][];
        let headerRow: any[] = [];
        for (let i = 0; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (!Array.isArray(row) || row.length === 0) continue;
          const currentNonEmpty = row.filter(
            (cell) => cell !== '' && cell != null
          );
          if (currentNonEmpty.length === 0) continue;
          const nextRow = jsonData[i + 1];
          let nextNonEmptyCount = 0;
          if (Array.isArray(nextRow) && nextRow.length > 0) {
            nextNonEmptyCount = nextRow.filter(
              (cell) => cell !== '' && cell != null
            ).length;
          }
          if (nextNonEmptyCount > currentNonEmpty.length) {
            headerRow = nextRow;
          } else {
            headerRow = row;
          }
          break;
        }
        const columnNames = headerRow.filter(
          (name) => name !== '' && name != null
        );
        resolve(columnNames);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    if (file.name.endsWith('.csv')) {
      reader.readAsText(file);
    } else {
      reader.readAsBinaryString(file);
    }
  });
};

interface UploadTransactionsDrawerProps {
  organisationId?: string;
}

export const UploadTransactionsDrawer: React.FC<
  UploadTransactionsDrawerProps
> = ({ organisationId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedBox, setSelectedBox] =
    React.useState<UploadTransactionsSelection | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const isUploadDisabled = !selectedFile || selectedBox === null;

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleUploadFile = async () => {
    if (!selectedFile || selectedBox === null) {
      setError(t('organisation.select-migration-option-and-file'));
      return;
    }

    if (!isValidExcelOrCsvFile(selectedFile)) {
      dispatch(
        turnMessageOn({
          title: t('organisation.wrong-file-type-title'),
          description: t('organisation.wrong-file-type-description'),
          type: MessageStates.WARNING,
        })
      );
      return;
    }

    setError(null);

    try {
      // Extract column names from the file
      const columnNames = await extractColumnNamesFromFile(selectedFile);

      // Store file & selection in organisation store
      dispatch(setUploadTransactionsTemp(selectedFile, selectedBox));

      // Close the drawer
      dispatch(turnDrawerOff());

      // Open modal with column names (use dedicated EXCEL_TRANSACTIONS window type)
      dispatch(
        turnModalOn({
          title: t('organisation.map-import-fields-title'),
          type: WindowType.EXCEL_TRANSACTIONS,
          maskClosable: false,
          component: (
            <UploadTransactionsColumnsModal
              columnNames={columnNames}
              fileName={selectedFile.name}
              organisationId={organisationId}
            />
          ),
        })
      );
    } catch (error) {
      setError(t('organisation.upload-failed-description'));
    }
  };

  const showCsvInfoModal = () => {
    dispatch(
      turnModalOn({
        title: t('organisation.csv-format'),
        type: WindowType.FORM,
        component: (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <CardDescSpan>
              CSV (Comma-Separated Values) is a simple file format used to store
              tabular data. It's a text file where each line represents a row of
              data, and values are separated by commas. CSV files are widely
              compatible and can be opened in spreadsheet applications like
              Excel, Google Sheets, or any text editor. This format is ideal for
              importing transaction data into generate.TAX as it maintains data
              integrity and is easy to work with.
            </CardDescSpan>
          </div>
        ),
      })
    );
  };

  const showExcelInfoModal = () => {
    dispatch(
      turnModalOn({
        title: t('organisation.excel-format'),
        type: WindowType.FORM,
        component: (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <CardDescSpan>
              Microsoft Excel files (.xlsx or .xls) are spreadsheet files that
              can contain multiple sheets, formulas, and formatting. Excel files
              are perfect for organizing complex transaction data with multiple
              columns and rows. When uploading Excel files to generate.TAX, the
              system will automatically extract the transaction data and map it
              to the appropriate fields for tax return generation.
            </CardDescSpan>
          </div>
        ),
      })
    );
  };

  return (
    <DrawerContentWrapper>
      <InstructionText>
        {t('organisation.upload-transactions-drawer-intro-before-link')}{' '}
        <ClickableLink
          onClick={() =>
            window.open(SUPPORT_ARTICLE_URL, '_blank', 'noopener,noreferrer')
          }
        >
          {t('organisation.support-article')}
        </ClickableLink>{' '}
        {t('organisation.upload-transactions-drawer-intro-after-link')}
      </InstructionText>

      <GridContainer>
        {/* Blank Template */}
        <UploadButton
          $isSelected={selectedBox === UploadTransactionsSelection.BLANK}
          onClick={() => {
            setSelectedBox(UploadTransactionsSelection.BLANK);
            setError(null);
          }}
        >
          <ButtonContent>
            <ButtonText>{t('organisation.blank-template')}</ButtonText>
          </ButtonContent>
        </UploadButton>

        {/* generate TAX Logo */}
        <UploadButton
          $isSelected={selectedBox === UploadTransactionsSelection.GEN_TAX}
          onClick={() => {
            setSelectedBox(UploadTransactionsSelection.GEN_TAX);
            setError(null);
          }}
        >
          <ButtonContent>
            <GenTaxLogoImage src={GenTaxLogo} alt="generate.TAX" />
          </ButtonContent>
        </UploadButton>

        {/* Sage Logo */}
        <UploadButton
          $isSelected={selectedBox === UploadTransactionsSelection.SAGE}
          onClick={() => {
            setSelectedBox(UploadTransactionsSelection.SAGE);
            setError(null);
          }}
        >
          <ButtonContent>
            <LogoImage src={IconSage} alt="Sage" />
          </ButtonContent>
        </UploadButton>

        {/* Description placeholder - spans all 3 columns and keeps space even when empty */}
        <DescriptionPlaceholder style={{ gridColumn: '1 / -1' }}>
          {selectedBox !== null
            ? t(
                `organisation.upload-transactions-box-description-${selectedBox.toLowerCase()}`
              )
            : ''}
        </DescriptionPlaceholder>
      </GridContainer>

      <FileUploadSection>
        <FileUploadText>{t('organisation.drag-or-find-a-file')}</FileUploadText>

        <FileInputWrapper>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            accept=".xlsx,.xls,.csv"
            style={{ display: 'none' }}
          />
          <FileInput
            type="text"
            placeholder={t('organisation.upload-an-excel-file')}
            value={selectedFile?.name || ''}
            readOnly
          />
          <FileInputButton onClick={handleFileButtonClick}>
            <PlusOutlined />
          </FileInputButton>
        </FileInputWrapper>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <UploadFileButtonWrapper>
          <ScopeButton
            type="dark-orange"
            width="100%"
            height="40px"
            disabled={isUploadDisabled}
            onClick={handleUploadFile}
          >
            {t('organisation.upload-file')}
          </ScopeButton>
        </UploadFileButtonWrapper>
      </FileUploadSection>
    </DrawerContentWrapper>
  );
};
